import React, { useState } from 'react';
import {
  Search,
  Download,
  UserX,
  Calendar,
  TrendingDown,
  AlertCircle
} from 'lucide-react';
import moment from 'moment';
import { useListOptOutsQuery } from '@/client/api/whatsapp/list-opt-outs.query';

const OptOutsPage: React.FC = () => {
  const { data, isLoading } = useListOptOutsQuery();
  const optOuts = data?.data?.optouts || [];
  const totalCount = data?.data?.total_count ?? optOuts.length;

  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState<string>('all');

  const filteredOptOuts = optOuts.filter(optOut => {
    const matchesSearch =
      optOut.phone.includes(searchQuery) ||
      (optOut.reason || '').toLowerCase().includes(searchQuery.toLowerCase());

    if (dateFilter === 'all') return matchesSearch;

    const daysDiff = Math.floor((Date.now() / 1000 - optOut.created_at) / 86400);

    if (dateFilter === 'week' && daysDiff <= 7) return matchesSearch;
    if (dateFilter === 'month' && daysDiff <= 30) return matchesSearch;
    if (dateFilter === 'quarter' && daysDiff <= 90) return matchesSearch;

    return false;
  });

  const handleExport = () => {
    const rows = [['Phone', 'Opt-out Date', 'Reason'], ...optOuts.map(o => [
      o.phone,
      moment.unix(o.created_at).format('YYYY-MM-DD'),
      o.reason || '',
    ])];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'whatsapp-opt-outs.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Opt-outs Management</h1>
            <p className="text-sm text-gray-500 mt-1">Manage users who have opted out of WhatsApp communications</p>
          </div>
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export List
          </button>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Total Opt-outs</span>
              <UserX className="w-5 h-5 text-red-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
            <p className="text-xs text-gray-500 mt-1">All time</p>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">This Week</span>
              <Calendar className="w-5 h-5 text-orange-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {optOuts.filter(o => Math.floor((Date.now() / 1000 - o.created_at) / 86400) <= 7).length}
            </p>
            <p className="text-xs text-gray-500 mt-1">Last 7 days</p>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">This Month</span>
              <TrendingDown className="w-5 h-5 text-purple-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {optOuts.filter(o => Math.floor((Date.now() / 1000 - o.created_at) / 86400) <= 30).length}
            </p>
            <p className="text-xs text-gray-500 mt-1">Last 30 days</p>
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-yellow-900 mb-1">Important Notice</h3>
              <p className="text-sm text-yellow-800">
                Users who have opted out will not receive any WhatsApp messages. Ensure compliance with data protection regulations and respect user preferences.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by phone or reason..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Time</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
                <option value="quarter">Last 90 Days</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Opt-out Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reason
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading && (
                  <tr>
                    <td colSpan={3} className="px-6 py-4 text-sm text-gray-500 text-center">Loading...</td>
                  </tr>
                )}
                {filteredOptOuts.map((optOut) => (
                  <tr key={optOut.phone} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center text-white font-semibold">
                          {optOut.phone.slice(-2)}
                        </div>
                        <p className="text-sm font-medium text-gray-900">{optOut.phone}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {moment.unix(optOut.created_at).format('YYYY-MM-DD')}
                    </td>
                    <td className="px-6 py-4">
                      {optOut.reason ? (
                        <span className="text-sm text-gray-900">{optOut.reason}</span>
                      ) : (
                        <span className="text-sm text-gray-400 italic">No reason provided</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!isLoading && filteredOptOuts.length === 0 && (
            <div className="text-center py-12">
              <UserX className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No opt-outs found</h3>
              <p className="text-sm text-gray-500">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OptOutsPage;
